import { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ThemeProvider } from "@mui/material";

import {
  forgotPassword,
  resendOTP,
  verifyOTP,
} from "../../../services/authApi";
import Logo from "../../../assets/icons/olinlogo.png";
import Input from "../../common/particles.jsx/Input";
import Carousel from "../../common/particles.jsx/Carousel";
import { PasswordContext } from "../../context/ForgotPasswordProvider";
import { theme } from "../../constants/muiTheme";

const SEND_VERIFICATION_RETRY_DELAY_SECONDS = 180;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { email, setEmail } = useContext(PasswordContext);
  const [otpCode, setOtpCode] = useState("");
  const [isSubmitOTPSuccess, setIsSubmitOTPSuccess] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(
    SEND_VERIFICATION_RETRY_DELAY_SECONDS,
  );
  const [errorMessage, setErrorMessage] = useState("");

  const { mutate: mutateForgotPassword, isLoading: isLoadingForgotPassword } =
    useMutation({
      mutationFn: forgotPassword,
      onSuccess: () => {
        setIsSubmitOTPSuccess(true);
        setIsPressed(true);
      },
      onError: (err) => {
        setErrorMessage(err.id);
      },
    });

  const { mutate: mutateResendOTP } = useMutation({
    mutationFn: resendOTP,
    onSuccess: () => {
      setIsPressed(true);
    },
    onError: (err) => {
      setErrorMessage(err.message.id);
    },
  });

  const { mutate: mutateVerifyOTP, isLoading: isLoadingVerifyOTP } =
    useMutation({
      mutationFn: verifyOTP,
      onSuccess: () => {
        navigate("/forgot-password/new-password");
      },
      onError: (err) => {
        setErrorMessage(err.message.id);
      },
    });

  useEffect(() => {
    if (!isPressed) return;

    const interval = setInterval(() => {
      setCountdownSeconds((prevCount) => {
        if (prevCount === 0) {
          clearInterval(interval);
          setIsPressed(false);
          setCountdownSeconds(SEND_VERIFICATION_RETRY_DELAY_SECONDS);
          return prevCount;
        }
        return prevCount - 1;
      });

      return () => clearInterval(interval);
    }, 1000);
  }, [isPressed]);

  return (
    <ThemeProvider theme={theme}>
      <div className="bg-white ">
        <div className="flex flex-col-reverse lg:flex-row justify-center h-screen">
          <div className="order-first md:order-last bg-cover lg:block lg:w-2/3">
            <div className="flex items-center h-full px-20 bg-[#EAF3FE] bg-opacity-40">
              <div className="w-full my-10">
                <Carousel />
              </div>
            </div>
          </div>

          <div className="flex mt-20 w-full max-w-md px-6 mx-auto lg:w-2/6 ">
            <div className="flex-1">
              <div className="flex flex-col items-center justify-center text-center">
                <img
                  src={Logo}
                  alt=""
                  className="mt-16 lg:my-0 w-[100px] lg:w-[120px]"
                />
                {isSubmitOTPSuccess ? (
                  <>
                    <h2 className="text-[32px] font-bold text-center  text-black mt-3 lg:mt-20">
                      OTP Telah Dikirimkan
                    </h2>
                    <p className="mt-1 text-[14px] text-gray-500 ">
                      Silahkan cek OTP di email <br />
                      {email}
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="text-[32px] font-bold text-center  text-black mt-3 lg:mt-20">
                      Lupa password
                    </h2>
                    <p className="mt-1 text-[14px] text-gray-500 ">
                      Silahkan masukan email akun anda <br />
                      untuk mereset password anda.
                    </p>
                  </>
                )}
              </div>
              <div className="mt-8">
                {isSubmitOTPSuccess ? (
                  <>
                    <div className="w-full">
                      <div className="flex justify-between">
                        <label className="block mb-2 text-sm text-blue-400">
                          Kode OTP
                        </label>
                        {isPressed ? (
                          <p>
                            {new Date(countdownSeconds * 1000)
                              .toISOString()
                              .substring(14, 19)}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex justify-between items-center w-full">
                        <input
                          type="text"
                          name="OTP"
                          onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                          maxLength="6"
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setOtpCode(value);
                            setErrorMessage("");
                          }}
                          value={otpCode}
                          className={`block w-full px-4 py-2 text-gray-700 placeholder-gray-400 bg-white rounded-md border-2 ${
                            Boolean(errorMessage)
                              ? "border-red-500 focus:border-red-400 focus:ring-red-400 focus:ring focus:outline-none focus:ring-opacity-40"
                              : "border-gray-200 focus:border-blue-400 focus:ring-blue-400 focus:ring focus:outline-none focus:ring-opacity-40"
                          }`}
                        />
                      </div>

                      <div className="text-[12px] flex justify-between gap-4 w-full mt-2">
                        <p>Belum mendapatkan kode OTP?</p>
                        <p
                          className={`text-[12px] ${
                            isPressed
                              ? "text-gray-500"
                              : "text-[#FF6B00] hover:cursor-pointer"
                          }`}
                          onClick={() => {
                            if (isPressed) return;
                            mutateResendOTP({ email });
                          }}
                        >
                          Kirim Ulang
                        </p>
                      </div>

                      <p className="text-sm text-red-500">{errorMessage}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <Input
                      label="E-mail"
                      type="email"
                      name="email"
                      placeholder="Example@mail.com"
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setErrorMessage("");
                      }}
                    />
                    <p className="text-red-500 mt-[10px]">{errorMessage}</p>
                  </>
                )}
                <div className="mt-6">
                  {isSubmitOTPSuccess ? (
                    <button
                      className="text-[14px] w-full px-4 py-2 tracking-wide text-white transition-colors transform bg-blue-500 rounded-md focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-50 disabled:bg-gray-500"
                      disabled={
                        otpCode === "" ||
                        (otpCode.length !== 4 && otpCode.length !== 6) ||
                        isLoadingVerifyOTP
                      }
                      type="button"
                      onClick={() => mutateVerifyOTP({ email, code: otpCode })}
                    >
                      {isLoadingVerifyOTP ? "Loading" : "Kirim"}
                    </button>
                  ) : (
                    <button
                      className="text-[14px] w-full px-4 py-2 tracking-wide text-white transition-colors duration-200 transform bg-blue-500 rounded-md hover:bg-blue-400 focus:outline-none focus:bg-blue-400 focus:ring focus:ring-blue-300 focus:ring-opacity-50 disabled:bg-gray-500"
                      type="button"
                      disabled={email === "" || isLoadingForgotPassword}
                      onClick={() => mutateForgotPassword({ receiver: email })}
                    >
                      {isLoadingForgotPassword ? "Loading" : "Selanjutnya"}
                    </button>
                  )}
                </div>
                <div
                  className="my-2 flex justify-center hover:bg-gray-100 p-2 rounded-md text-[14px] cursor-pointer"
                  onClick={() => navigate(`/login`)}
                >
                  Kembali ke Login
                </div>

                {/* <div className="relative mt-6 h-px bg-gray-300">
                  <div className="absolute left-0 top-0 flex justify-center w-full -mt-2">
                    <span className="bg-white px-4 text-xs text-gray-500 uppercase">Belum Registrasi?</span>
                  </div>
                </div>
                <div className="mt-6">
                  <button className="w-full px-4 py-2 tracking-wide text-white transition-colors duration-200 transform bg-orange-500 rounded-md hover:bg-blue-400 focus:outline-none focus:bg-blue-400 focus:ring focus:ring-blue-300 focus:ring-opacity-50">
                    Daftar
                  </button>
                </div> */}
                <div className="flex flex-col items-center justify-center">
                  <p className="font-medium text-center text-[12px] mb-1">
                    Pelajari lebih lanjut tentang OLIN
                  </p>
                  <p className="text-[12px] border-1 text-center py-2 px-8 rounded-xl border-orange-500 w-fit text-amber-500 text-center">
                    Unduh Aplikasi
                  </p>
                </div>

                {/* <div className="mt-6 text-sm text-center text-gray-400">
                Don&#x27;t have an account yet? <p className="text-blue-500 underline focus:outline-none hover:underline focus:underline hover:underline">Sign up</p>.
              </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default ForgotPassword;
